/** How ninekeep will watch a vendor (TR-47): shown on the vendor's page and on a contract's review page. */

export interface SetupProposal {
  statusPage: { url: string; provider: string; otherSite: boolean } | null;
  coverage: {
    service: string;
    counts: { component: string; reason: string }[];
    outside: { component: string; reason: string }[];
  }[];
  checks: { name: string; url: string; foundOn: string }[];
  details: { gap: string; value: string; quote: string }[];
  questions: { topic: string; question: string; choices: string[]; pick: string }[];
  notes: string[];
}

export interface Setup {
  status: "working" | "ready" | "applied" | "failed";
  error: string | null;
  proposal: SetupProposal | null;
}

export interface Keep {
  statusPage: boolean;
  services: string[];
  checks: string[];
  details: string[];
}

/** Everything is kept to start with, except a status page on another site. */
export const defaultKeep = (p: SetupProposal | null | undefined): Keep => ({
  statusPage: !!p?.statusPage && !p.statusPage.otherSite,
  services: p?.coverage.map((c) => c.service) ?? [],
  checks: p?.checks.map((c) => c.url) ?? [],
  details: p?.details.map((x) => x.gap) ?? [],
});

const GAP: Record<string, string> = {
  account_reference: "Account number",
  claims_email: "Claims email",
  fee: "Fee",
  renewal: "Renewal",
};

export function SetupChoices({
  p,
  vendor,
  keep: k,
  onChange: setKeep,
}: {
  p: SetupProposal;
  vendor: string;
  keep: Keep;
  onChange: (k: Keep) => void;
}) {
  const toggle = (list: "services" | "checks" | "details", v: string) =>
    setKeep({
      ...k,
      [list]: k[list].includes(v) ? k[list].filter((x) => x !== v) : [...k[list], v],
    });
  return (
    <div className="flex flex-col gap-4 text-sm">
      {p.statusPage && (
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={k.statusPage}
            onChange={(e) => setKeep({ ...k, statusPage: e.target.checked })}
          />
          <span>
            Read the status page at{" "}
            <span className="font-medium break-all">{p.statusPage.url}</span>
            {p.statusPage.otherSite && (
              <span className="text-warn"> (on another site; check it's really {vendor}'s)</span>
            )}
          </span>
        </label>
      )}
      {p.coverage.map((c) => (
        <label key={c.service} className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={k.services.includes(c.service)}
            onChange={() => toggle("services", c.service)}
          />
          <span>
            For <span className="font-medium">{c.service}</span>, count{" "}
            {c.counts.map((x) => x.component).join(", ")}
            {c.outside.length > 0 && (
              <span className="text-muted">
                ; not {c.outside.map((x) => x.component).join(", ")}
              </span>
            )}
          </span>
        </label>
      ))}
      {p.checks.map((c) => (
        <label key={c.url} className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={k.checks.includes(c.url)}
            onChange={() => toggle("checks", c.url)}
          />
          <span>
            Check <span className="break-all">{c.url}</span> every minute
            {c.foundOn === "contract" && (
              <span className="text-muted"> (named in the contract)</span>
            )}
          </span>
        </label>
      ))}
      {p.details.map((x) => (
        <label key={x.gap} className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={k.details.includes(x.gap)}
            onChange={() => toggle("details", x.gap)}
          />
          <span>
            {GAP[x.gap]}: <span className="font-medium">{x.value}</span>{" "}
            <span className="text-muted">(“{x.quote}”)</span>
          </span>
        </label>
      ))}
      {p.questions.map((q) => (
        <p key={q.topic} className="text-muted">
          {q.question} ninekeep will use: <span className="text-ink">{q.pick}</span>.
        </p>
      ))}
      {p.notes.map((n) => (
        <p key={n} className="text-muted">
          {n}
        </p>
      ))}
    </div>
  );
}
